/* ============================================================
   FILTROUS — Boost product-card cart + quantity handler
   ------------------------------------------------------------
   ONE global delegated handler, loaded in layout/theme.liquid.
   Survives Boost server-side re-renders (search + collection)
   because it binds to `document`, not to the cards themselves.

   Responsibilities:
     1. .fn-card__qty-btn  — increment / decrement the sibling
        qty input. MUST NOT navigate (Boost wraps product items
        in a click handler that routes to the homepage; without
        stopPropagation a +/- click bubbled up and navigated).
     2. .fn-card__atc-btn  — AJAX add-to-cart and STAY on page
        (this theme has no cart drawer; it's a cart-page theme),
        then update the header cart count and flash "Added".

   The corresponding Boost `src/product-item/main.liquid` is now
   clean semantic HTML with data-* attributes only — no inline JS.
   ============================================================ */
(function () {
  'use strict';

  // Decrement / increment the quantity input that lives next to the
  // clicked button, clamped to a minimum of 1.
  //
  // Direction resolution is layered so this works with the new clean
  // main.liquid (data-qty-step) AND any older markup still pasted in Boost
  // (no data attr) — it never silently steps the wrong way:
  //   1. data-qty-step="down" | "up"  (authoritative, new markup)
  //   2. aria-label contains "decrease"            (old markup hint)
  //   3. button is the FIRST .fn-card__qty-btn in the group = minus
  function stepDirection(btn, wrap) {
    var attr = btn.getAttribute('data-qty-step');
    if (attr === 'down') return -1;
    if (attr === 'up') return 1;

    var label = (btn.getAttribute('aria-label') || '').toLowerCase();
    if (label.indexOf('decrease') > -1 || label.indexOf('minus') > -1) return -1;
    if (label.indexOf('increase') > -1 || label.indexOf('plus') > -1) return 1;

    var btns = wrap.querySelectorAll('.fn-card__qty-btn');
    if (btns.length && btns[0] === btn) return -1; // first = minus
    return 1;
  }

  function stepQty(btn) {
    var wrap = btn.closest('.fn-card__qty');
    if (!wrap) return;
    var input = wrap.querySelector('.fn-card__qty-input');
    if (!input) return;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 1;
    var next = current + stepDirection(btn, wrap);
    if (next < 1) next = 1;
    input.value = next;
  }

  // Read the quantity for a given add-to-cart button.
  function readQty(atcBtn) {
    var footer = atcBtn.closest('.fn-card__footer') || atcBtn.closest('.fn-card');
    var input = footer ? footer.querySelector('.fn-card__qty-input') : null;
    var q = input ? parseInt(input.value, 10) : 1;
    if (isNaN(q) || q < 1) q = 1;
    return q;
  }

  // Update every header cart-count badge and make it visible.
  function updateCartCount(count) {
    document
      .querySelectorAll('.fn-nav__cart-count, .site-header-cart--count')
      .forEach(function (el) {
        el.textContent = count;
        el.classList.add('visible');
      });
  }

  function addToCart(atcBtn) {
    var variantId = atcBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    var qty = readQty(atcBtn);
    var original = atcBtn.innerHTML;
    atcBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        atcBtn.innerHTML = 'Added ✓';
        setTimeout(function () {
          atcBtn.innerHTML = original;
          atcBtn.disabled = false;
        }, 1500);
      })
      .catch(function () {
        atcBtn.disabled = false;
      });
  }

  // Single delegated listener. Capturing on document means we run
  // before Boost's bubbling product-item navigation handler, and
  // stopPropagation() prevents the click from ever reaching it.
  document.addEventListener(
    'click',
    function (e) {
      var qtyBtn = e.target.closest('.fn-card__qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        stepQty(qtyBtn);
        return;
      }

      var atcBtn = e.target.closest('.fn-card__atc-btn');
      if (atcBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (atcBtn.disabled) return;
        addToCart(atcBtn);
        return;
      }

      // ANY click on the card (photo, title, whitespace) — Boost's product-item
      // handler hijacks it and routes to the HOMEPAGE. Always stop that handler.
      // Then route the click correctly:
      //   - real <a href>  → let it navigate naturally (don't preventDefault)
      //   - footer controls → do nothing (qty/ATC already handled above)
      //   - anywhere else  → go to the card's product page
      var card = e.target.closest('.fn-card');
      if (card) {
        e.stopPropagation();                       // beat Boost's homepage misfire
        if (e.target.closest('a[href]')) return;   // anchor handles its own nav
        if (e.target.closest('.fn-card__footer')) return; // controls zone — no nav
        var imgLink = card.querySelector('.fn-card__image-wrap');
        var href = imgLink ? imgLink.getAttribute('href') : null;
        if (href) {
          e.preventDefault();
          window.location.href = href;
        }
        return;
      }
    },
    true // capture phase — beat Boost's handler to the click
  );
})();
